import { updateClipContext } from "building-generation/elements/multiGableRoofClipContext";
import { getFirstSlope } from "building-generation/elements/roofs/roofSlopes";
import type {
  RoofVolumeGenerationStageInput,
  RoofVolumeGenerationStageResult,
} from "building-types/roofVolumes/roofVolumeGenerationTypes";
import { GableRoofType, RoofBoundaryType } from "building-types/settings/settingsTypesEnum";
import { getMaxDepthErrorHandle } from "doc/building/buildingGenerationIssuesTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { buildEdgePolygon, buildBSPNode } from "geom/bsp";
import type { NodeBSP, PolygonBSP } from "geom/bspTypes";
import { polyLine2EdgeCount, polyLine2EdgeStart, polyLine2EdgeEnd } from "geom/polyLine2";
import { getSoffitDepth } from "../../../elements/roofTrimUtility";

/**
 * Generates the clipping information, including:
 * - updating the roof system clip context
 * - overhang clipping volumes
 * - extra clipping volumes for walls, end faces...
 */
export function generateRoofVolumeClippingVolumes({
  roofVolumeOptions,
  roofVolume,
  temporaries,
}: RoofVolumeGenerationStageInput): RoofVolumeGenerationStageResult {
  const {
    id: subRoofId,
    boundaryType,
    baseHeight,
    leftSideSlopes,
    rightSideSlopes,
    heelHeightOffsets,
    perimeters,
    isDormer,
    isFloatingVirtualRoof,
    roofType,
    extensions,
    verticalRails,
    shedRoofHalfFace,
    shedRoofWallHalfFace,
    extrusion,
    rectangularOverhangs,
    startFaces,
    endFaces,
    exactEdges,
    roofTopFaces,
    unclippedRoofTopFaces,
    roofTopFacesWithOverlap,
    peakHeight,
  } = roofVolume;

  const { extraHipFaces } = temporaries;

  const { face: startFrontFace, discardFace: startDiscardFrontFace } = startFaces;
  const { face: endFrontFace, discardFace: endDiscardFrontFace } = endFaces;

  const { baseLeftOverhang, baseRightOverhang } = rectangularOverhangs;

  const { hasStartExtension, hasEndExtension } = extensions;
  const { wallPerimeter, offsetWallPerimeter } = perimeters;

  const { clipContext, inputPerimeters, roofInputSettings, issues, wallPlaneMasses } = roofVolumeOptions;
  const { roofSettings } = roofInputSettings;
  const { peakDirection } = roofSettings;

  const { srcPerimeter } = inputPerimeters;
  const { slope: firstSlopeLeft } = getFirstSlope(leftSideSlopes);
  const { slope: firstSlopeRight } = getFirstSlope(rightSideSlopes);
  const wallExtensionHeightLeft = firstSlopeLeft * baseLeftOverhang;
  const wallExtensionHeightRight = firstSlopeRight * baseRightOverhang;

  const { isStartTruncated, isEndTruncated } = verticalRails;

  const maxDepthErrorHandle = getMaxDepthErrorHandle(issues, { type: "ROOF", id: subRoofId });

  // end face clipping
  let clipEndFacesBSP: NodeBSP | null = null;
  if (boundaryType === RoofBoundaryType.CLIP_TO_PERIMETER) {
    const clipEndPolygons: PolygonBSP[] = [];
    for (let i = 0, l = polyLine2EdgeCount(wallPerimeter); i < l; i++) {
      const [ax, ay] = polyLine2EdgeStart(wallPerimeter, i);
      const [bx, by] = polyLine2EdgeEnd(wallPerimeter, i);

      const polygon = buildEdgePolygon(bx, by, 0, ax, ay, 0, 0, 0, 1);
      if (!polygon) {
        continue;
      }

      clipEndPolygons.push(polygon);
    }

    clipEndFacesBSP = buildBSPNode({
      polygons: clipEndPolygons,
      epsilon: MULTIGABLE_BSP_EPSILON,
      maxDepthErrorHandle,
    });
  }

  // update clipping info
  const { clipBottomBSP, discardFriezeBSP, discardWallsBSP, discardStartWallBSP, discardEndWallBSP, roofTopBSP } =
    updateClipContext({
      clipContext,
      roofSettings,
      subRoofId,
      hasStartExtension,
      hasEndExtension,
      peakDirection,
      wallPerimeter,
      offsetWallPerimeter,
      srcPerimeter,
      exactEdges,
      heelHeightOffsets,
      baseHeight,
      wallExtensionHeightLeft,
      wallExtensionHeightRight,
      roofTopFaces,
      roofTopFacesWithOverlap,
      unclippedRoofTopFaces,
      extrusion,
      startFrontFace: startFrontFace ?? undefined,
      endFrontFace: endFrontFace ?? undefined,
      startDiscardFrontFace: startDiscardFrontFace ?? undefined,
      endDiscardFrontFace: endDiscardFrontFace ?? undefined,
      shedRoofHalfFace,
      shedRoofWallHalfFace,
      clipEndFacesBSP: clipEndFacesBSP ?? undefined,
      extraHipFaces,
      soffitDepth: getSoffitDepth(roofSettings),
      isDormer,
      isShedRoof: roofType !== GableRoofType.DEFAULT,
      isFloatingVirtualRoof,
      isStartTruncated,
      isEndTruncated,
      maxDepthErrorHandle,
      wallPlaneMasses,
      peakHeight,
    });

  return {
    roofVolume: {
      clipBottomBSP,
      discardFriezeBSP,
      discardWallsBSP,
      discardStartWallBSP,
      discardEndWallBSP,
      roofTopBSP,
    },
  };
}
